import { Badge, Card, Modal, btnPrimary, btnSecondary, btnDanger, inputStyle, labelStyle, statusColor } from "./shared";
import Leases from "./Leases";

const normalizeAddr = (addr) => (addr || "")
  .replace(/[\u00a0\u00ad\u200b\u200c\u200d\u2009\u202f\u2028\u2029\u3000\t\r\n]+/g, " ")
  .toLowerCase()
  .replace(/\bavenue\b/g, "ave").replace(/\bstreet\b/g, "st")
  .replace(/\bdrive\b/g, "dr").replace(/\bboulevard\b/g, "blvd")
  .replace(/\blane\b/g, "ln").replace(/\bcourt\b/g, "ct")
  .replace(/\broad\b/g, "rd").replace(/\bplace\b/g, "pl")
  .replace(/\bwest\b/g, "west")
  .replace(/\bw\b/g, "west").replace(/\be\b/g, "east")
  .replace(/\bn\b/g, "north").replace(/\bs\b/g, "south")
  .replace(/#\s*/g, " ")
  .replace(/\s+(unit|apt|apartment|suite|ste|no\.?|num|floor)\s*[\w-]*\s*$/i, "")
  .replace(/[.,]/g, "").replace(/\s+/g, " ").trim();

export default function Properties({
  session,
  properties, tenants, rentIncreases, leases, getLeasePdfUrl,
  propTab, setPropTab,
  can,
  seedDefaultProperties,
  setShowAddProperty,
  setSelectedTenant, setPage,
  editProperty, setEditProperty, savePropertyEdit, deleteProperty,
  applyRentIncrease, deleteRentIncrease,
  newRentIncrease, setNewRentIncrease,
  showAddRentIncrease, setShowAddRentIncrease, addRentIncrease,
  showAddProperty, newProperty, setNewProperty, addProperty,
  syncPropertiesFromTenants,
  setSelectedProperty,
  setShowLeaseWizard,
}) {
  const getPropertyTenants = (prop) => {
    const pa = normalizeAddr(prop.address);
    const paNum   = (pa.match(/^\d+/) || [])[0];
    const paWords = pa.split(" ").filter(w => w.length > 3 && !/^\d+$/.test(w));
    return tenants.filter(t => {
      const ta = normalizeAddr(t.address);
      if (!ta || !pa) return false;
      if (ta === pa) return true;
      if (ta.startsWith(pa + ",") || ta.startsWith(pa + " ")) return true;
      if (pa.startsWith(ta + ",") || pa.startsWith(ta + " ")) return true;
      if (ta.includes(pa)) return true;
      if (paNum && (ta.match(/^\d+/) || [])[0] === paNum && paWords.every(w => ta.includes(w))) return true;
      return false;
    });
  };

  const thLbl = { fontSize: 11, color: "#9B9690", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 500 };

  return (
    <>
      {/* ── Properties page ── */}
      <div className="app-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 32, color: "#2B2B2B", margin: 0 }}>Properties</h1>
          <div style={{ color: "#9B9690", marginTop: 4, fontSize: 14 }}>{properties.length} properties · {tenants.length} tenants</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {propTab === "leases" && <button style={btnSecondary} onClick={() => setShowAddRentIncrease(true)}>📈 Schedule Rent Increase</button>}
          {propTab === "properties" && can.editTenant && (
            <>
              <button style={{ ...btnSecondary, fontSize: 12 }} onClick={syncPropertiesFromTenants}>↻ Sync from Tenants</button>
              <button style={btnPrimary} onClick={() => setShowAddProperty(true)}>+ Add Property</button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e0dbd0" }}>
        {[["properties","🏠 Properties"],["leases","📋 Leases"]].map(([tab, label]) => (
          <button key={tab} onClick={() => setPropTab(tab)} style={{
            border: "none", background: "none", padding: "10px 18px", cursor: "pointer",
            fontWeight: propTab === tab ? 700 : 500, fontSize: 14,
            color: propTab === tab ? "#163C2F" : "#9B9690",
            borderBottom: propTab === tab ? "2px solid #163C2F" : "2px solid transparent",
            fontFamily: "'Manrope', sans-serif", marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {/* Properties sub-tab */}
      {propTab === "properties" && (
        <>
          {properties.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 20px" }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🏠</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#163C2F", marginBottom: 8 }}>No properties added yet</div>
              <div style={{ fontSize: 13, color: "#9B9690", marginBottom: 24 }}>Add your properties to track units and tenant assignments.</div>
              {can.editTenant && (
                <button style={btnPrimary} onClick={() => setShowAddProperty(true)}>+ Add Property</button>
              )}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
            {properties.map((prop, idx) => {
              const propTenants = getPropertyTenants(prop);
              const occupied = propTenants.length;
              const vacant = Math.max(0, (prop.units || 1) - occupied);
              const pct = prop.units > 0 ? Math.round((occupied / (prop.units || 1)) * 100) : 0;
              return (
                <Card
                  key={prop.id || idx}
                  onClick={() => { if (setSelectedProperty) { setSelectedProperty(prop); setPage("PropertyDetail"); } }}
                  style={{ cursor: setSelectedProperty ? "pointer" : "default", transition: "box-shadow 0.15s, border-color 0.15s" }}
                  onMouseEnter={e => { if (setSelectedProperty) e.currentTarget.style.boxShadow = "0 4px 14px rgba(22,60,47,0.12)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 17, color: "#163C2F" }}>{prop.address}</div>
                      <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2 }}>{prop.units || 1} unit{(prop.units || 1) > 1 ? "s" : ""} · {occupied} occupied · {vacant} vacant</div>
                    </div>
                    {can.editTenant && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={e => { e.stopPropagation(); setEditProperty({ ...prop }); }} style={{ ...btnSecondary, fontSize: 12, padding: "4px 9px" }}>✏️</button>
                        <button onClick={e => { e.stopPropagation(); deleteProperty(prop.id); }} style={{ ...btnDanger, padding: "4px 9px" }}>🗑</button>
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#9B9690", marginBottom: 3 }}>
                      <span>Occupancy</span><span>{pct}%</span>
                    </div>
                    <div style={{ height: 4, background: "#E8E4DC", borderRadius: 2 }}>
                      <div style={{ height: "100%", background: "#163C2F", borderRadius: 2, width: `${pct}%`, transition: "width 0.3s" }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {propTenants.map((t, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "#F5F1EA", borderRadius: 8 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                          <div style={{ fontSize: 11, color: "#9B9690" }}>
                            {t.property || "Main Unit"}
                            {t.tenant_type === "non_paying"
                              ? ` · ${t.non_paying_subtype === "rent_free" ? "Rent-Free" : "Expenses Only"}`
                              : t.rent ? ` · $${t.rent.toLocaleString()}/mo` : ""}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <Badge label={t.status || "active"} color={statusColor[t.status] || "#22c55e"} />
                          <button onClick={e => { e.stopPropagation(); setPage("Tenants"); setTimeout(() => setSelectedTenant(t), 50); }} style={{ ...btnSecondary, fontSize: 11, padding: "3px 8px" }}>View</button>
                        </div>
                      </div>
                    ))}
                    {Array.from({ length: vacant }).map((_, i) => (
                      <div key={`v${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "#FFFFFF", borderRadius: 8, border: "1px dashed #d4cfc4" }}>
                        <div style={{ fontSize: 13, color: "#9B9690", fontStyle: "italic" }}>Vacant unit</div>
                        <Badge label="Vacant" color="#22c55e" />
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Leases sub-tab */}
      {propTab === "leases" && (
        <Leases
          tenants={tenants} rentIncreases={rentIncreases} leases={leases} getLeasePdfUrl={getLeasePdfUrl}
          setNewRentIncrease={setNewRentIncrease} setShowAddRentIncrease={setShowAddRentIncrease}
          applyRentIncrease={applyRentIncrease} deleteRentIncrease={deleteRentIncrease}
          setShowLeaseWizard={setShowLeaseWizard}
        />
      )}

      {/* ── Modals ── */}

      {/* Add Property Modal */}
      {showAddProperty && (
        <Modal title="Add Property" onClose={() => setShowAddProperty(false)} onSave={addProperty}>
          <div>
            <label style={labelStyle}>Street Address *</label>
            <input type="text" placeholder="e.g. 155 West Siino Ave" value={newProperty.address} onChange={e => setNewProperty(s => ({ ...s, address: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>City *</label>
              <input type="text" placeholder="e.g. Pittsburg" value={newProperty.city} onChange={e => setNewProperty(s => ({ ...s, city: e.target.value }))} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>State *</label>
              <select value={newProperty.state} onChange={e => setNewProperty(s => ({ ...s, state: e.target.value }))} style={inputStyle}>
                <option value="">Select</option>
                {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Zip *</label>
              <input type="text" placeholder="94565" value={newProperty.zip} onChange={e => setNewProperty(s => ({ ...s, zip: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Number of Units</label>
            <input type="number" min="1" max="100" value={newProperty.units} onChange={e => setNewProperty(s => ({ ...s, units: e.target.value }))} style={{ ...inputStyle, maxWidth: 120 }} />
          </div>
          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea value={newProperty.notes} onChange={e => setNewProperty(s => ({ ...s, notes: e.target.value }))} style={{ ...inputStyle, height: 72, resize: "vertical" }} placeholder="Any notes about this property…" />
          </div>
        </Modal>
      )}

      {/* Edit Property Modal */}
      {editProperty && (
        <Modal title="Edit Property" onClose={() => setEditProperty(null)} onSave={savePropertyEdit}>
          <div>
            <label style={labelStyle}>Street Address</label>
            <input type="text" value={editProperty.address} onChange={e => setEditProperty(s => ({ ...s, address: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Number of Units</label>
            <input type="number" min="1" max="100" value={editProperty.units} onChange={e => setEditProperty(s => ({ ...s, units: e.target.value }))} style={{ ...inputStyle, maxWidth: 120 }} />
          </div>
          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea value={editProperty.notes || ""} onChange={e => setEditProperty(s => ({ ...s, notes: e.target.value }))} style={{ ...inputStyle, height: 72, resize: "vertical" }} />
          </div>
        </Modal>
      )}

      {/* Schedule Rent Increase Modal */}
      {showAddRentIncrease && (
        <Modal title="Schedule Rent Increase" onClose={() => setShowAddRentIncrease(false)} onSave={addRentIncrease}>
          <div>
            <label style={labelStyle}>Tenant</label>
            <select value={newRentIncrease.tenant} onChange={e => setNewRentIncrease(s => ({ ...s, tenant: e.target.value }))} style={inputStyle}>
              <option value="">Select tenant…</option>
              {tenants.filter(t => !t.is_complimentary).map(t => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Effective Date</label>
            <input type="date" value={newRentIncrease.effective_date} onChange={e => setNewRentIncrease(s => ({ ...s, effective_date: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>New Monthly Rent ($)</label>
            <input type="number" min="0" value={newRentIncrease.new_rent} onChange={e => setNewRentIncrease(s => ({ ...s, new_rent: e.target.value }))} style={inputStyle} placeholder="e.g. 1500" />
          </div>
          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <input type="text" value={newRentIncrease.notes} onChange={e => setNewRentIncrease(s => ({ ...s, notes: e.target.value }))} style={inputStyle} placeholder="e.g. Annual increase per lease terms" />
          </div>
        </Modal>
      )}
    </>
  );
}
